/**
 * "Mấy giờ rồi bên chỗ họ" — dùng chung cho ô chọn người, nhãn hạn việc, ô chọn hạn.
 *
 * Sếp Phú 02/09/2026: chọn nhân viên là phải thấy ngay múi giờ, thành phố và **giờ hiện tại**
 * của người đó. Bắt người giao việc tự nhẩm "Toronto giờ là mấy" trước khi hẹn deadline là
 * cách chắc chắn để có những cái hẹn sai giờ.
 *
 * ⚠️ Mọi hàm ở đây phải chịu được múi giờ RÁC. Tên múi đến từ máy của người dùng (app đọc từ
 * hệ điều hành) và từ dữ liệu cũ; `Intl` ném `RangeError` với tên lạ, mà một cái nhãn phụ thì
 * không đáng làm trắng cả màn hình. Hỏng ⇒ trả `null`, chỗ gọi tự ẩn.
 */

/** Giờ hiện tại ở múi `tz`, dạng `14:05`. `null` nếu tên múi không dùng được. */
export function gioTaiMui(tz, now = new Date()) {
  if (!tz || typeof tz !== 'string') return null;
  try {
    // Locale để TRỐNG ⇒ theo máy người xem, đúng thói quen của họ. `hour12: false` đủ để ra
    // 24 giờ ở mọi locale, không cần chốt 'en-GB'.
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
      numberingSystem: 'latn',
    }).format(now);
  } catch {
    return null;
  }
}

/**
 * Lệch giờ so với máy người đang xem, dạng `+11h` / `−3h30` / `cùng giờ`.
 *
 * Tính bằng cách so hai chuỗi ngày-giờ đọc như UTC, KHÔNG trừ offset cứng: nửa số múi giờ có
 * đổi giờ mùa, và có múi lệch 30 hoặc 45 phút (Ấn Độ, Nepal, Chatham).
 */
export function lechSoVoiToi(tz, now = new Date()) {
  if (!tz || typeof tz !== 'string') return null;
  try {
    const doc = (zone) => {
      const p = {};
      // ⚠️ `numberingSystem: 'latn'` là BẮT BUỘC ở đây, không phải cho đẹp: máy đặt tiếng Ả
      // Rập hay Bengali trả về chữ số bản địa (٠١٢), `parseInt` ra `NaN`, và phép lệch giờ
      // hỏng câm. Chốt hệ chữ số thay vì chốt locale — locale vẫn theo máy người xem.
      for (const part of new Intl.DateTimeFormat(undefined, {
        timeZone: zone, hour12: false, numberingSystem: 'latn',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }).formatToParts(now)) {
        if (part.type !== 'literal') p[part.type] = parseInt(part.value, 10);
      }
      return Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second);
    };
    const phut = Math.round((doc(tz) - doc(Intl.DateTimeFormat().resolvedOptions().timeZone)) / 60000);
    if (phut === 0) return { phut: 0, nhan: null };
    const dau = phut > 0 ? '+' : '−';
    const abs = Math.abs(phut);
    const gio = Math.floor(abs / 60);
    const du = abs % 60;
    return { phut, nhan: `${dau}${gio}h${du ? String(du).padStart(2, '0') : ''}` };
  } catch {
    return null;
  }
}

/**
 * Tên IANA cũ → tên hiện đại. CHỈ để HIỆN CHỮ, không dùng để tính toán.
 *
 * `Asia/Saigon` và `Asia/Ho_Chi_Minh` là CÙNG MỘT múi giờ, nhưng cắt đuôi chuỗi ra thì thành
 * hai tên khác nhau — và trên màn đặt hạn, hai tab cùng 16:45 mà một cái ghi "Ho Chi Minh",
 * một cái ghi "Saigon" thì người dùng tưởng là hai nơi (sếp Phú soi 02/09). Trớ trêu là chính
 * trình duyệt đẩy ngược về tên cũ: Chrome canonicalize `Asia/Ho_Chi_Minh` → `Asia/Saigon`.
 *
 * Bảng này CỐ TÌNH ngắn — chỉ những cặp thật sự gặp. Thiếu một cặp thì hậu quả là tên cũ hiện
 * ra, không phải sai giờ.
 */
const TEN_HIEN_DAI = {
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Europe/Kiev': 'Europe/Kyiv',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
};

/**
 * Gom mọi bí danh của một múi giờ về MỘT chuỗi — `US/Pacific` và `America/Los_Angeles` phải ra
 * cùng một thứ, không thì hai tab cùng giờ lại hiện hai tên.
 *
 * Hai nhịp: trình duyệt gom bí danh trước (`resolvedOptions`), rồi mới đổi sang tên hiện đại.
 * Làm ngược lại là hụt, vì trình duyệt có thể đẩy về đúng cái tên cũ mình vừa đổi đi.
 */
export function chuanHoaMui(tz) {
  if (!tz || typeof tz !== 'string') return null;
  let goc = tz;
  try {
    goc = new Intl.DateTimeFormat('en', { timeZone: tz }).resolvedOptions().timeZone || tz;
  } catch {
    return null;   // tên múi giờ rác — chỗ gọi tự ẩn
  }
  return TEN_HIEN_DAI[goc] || goc;
}

/** Thành phố đọc được từ tên IANA: `America/Toronto` → `Toronto`, `Asia/Saigon` → `Ho Chi Minh`. */
export function thanhPhoCuaMui(tz) {
  const chuan = chuanHoaMui(tz);
  if (!chuan) return null;
  const last = chuan.split('/').pop();
  return last ? last.replace(/_/g, ' ') : null;
}

/**
 * Một dòng gọn để dán cạnh tên người: `Toronto · 09:14 · −11h`.
 * `workCity` có thì ưu tiên (ai làm ở Oakville mà múi giờ là Toronto).
 */
export function motDongVeMui(tz, workCity) {
  const gio = gioTaiMui(tz);
  if (!gio) return null;
  const lech = lechSoVoiToi(tz);
  return [workCity || thanhPhoCuaMui(tz), gio, lech?.nhan].filter(Boolean).join(' · ');
}

// ─── Quy giờ ĐỒNG HỒ giữa hai múi ────────────────────────────────────────────────────────
//
// CA = VIỆC (sếp Phú 25/09/2026): hạn của một ca là GIỜ TAN theo giờ của BÀN XẾP VIỆC (Toronto
// của Sky), nhưng người đặt hạn gõ giờ theo tab đồng hồ đang bật (giờ dự án / người thực hiện /
// của tôi — ngồi Việt Nam thì «17:00» của tôi là 06:00 Toronto). Hai hàm dưới quy qua MỐC
// TUYỆT ĐỐI, đọc lệch bằng `Intl` tại đúng khoảnh khắc đó — không trừ offset cứng: nửa số múi có
// giờ mùa, cộng cứng là lệch một tiếng suốt nửa năm.

const p2z = (n) => String(n).padStart(2, '0');

/** Các phần ngày-giờ của khoảnh khắc `ms` theo đồng hồ ở múi `tz` (chữ số latn — xem lechSoVoiToi). */
function phanTaiMui(ms, tz) {
  const p = {};
  for (const part of new Intl.DateTimeFormat(undefined, {
    timeZone: tz, hour12: false, numberingSystem: 'latn',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ms))) {
    if (part.type !== 'literal') p[part.type] = parseInt(part.value, 10);
  }
  return p;
}

/** Múi `tz` lệch UTC bao nhiêu mili-giây TẠI khoảnh khắc `ms`. */
function lechMs(ms, tz) {
  const p = phanTaiMui(ms, tz);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) - Math.floor(ms / 1000) * 1000;
}

/**
 * 'YYYY-MM-DD' + 'HH:mm' đọc theo đồng hồ ở múi `tz` → Date (khoảnh khắc tuyệt đối). `null` nếu
 * dữ liệu hoặc tên múi hỏng. Đọc lệch HAI lần: sát giờ đổi mùa, lệch tại «giờ đoán» khác lệch tại
 * khoảnh khắc thật.
 */
export function mocCuaGioTaiMui(ymd, hm, tz) {
  if (!ymd || !hm || !tz) return null;
  try {
    const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
    const [h, mi] = String(hm).slice(0, 5).split(':').map(Number);
    if (![y, m, d, h, mi].every(Number.isFinite)) return null;
    const nhuUtc = Date.UTC(y, m - 1, d, h, mi);
    let ms = nhuUtc - lechMs(nhuUtc, tz);
    ms = nhuUtc - lechMs(ms, tz);
    return new Date(ms);
  } catch {
    return null;
  }
}

/** Khoảnh khắc → `{ ymd, hm }` theo đồng hồ ở múi `tz`. `null` nếu tên múi hỏng. */
export function gioCuaMocTaiMui(moc, tz) {
  if (!moc || !tz) return null;
  try {
    const p = phanTaiMui(moc.getTime(), tz);
    return { ymd: `${p.year}-${p2z(p.month)}-${p2z(p.day)}`, hm: `${p2z(p.hour % 24)}:${p2z(p.minute)}` };
  } catch {
    return null;
  }
}

/**
 * Giờ đồng hồ ở múi `tu` → giờ đồng hồ ở múi `sang`, cùng một khoảnh khắc: `{ ymd, hm }`.
 * Thiếu một trong hai múi, hoặc hai múi trùng nhau ⇒ giữ nguyên (không quy gì).
 */
export function quyGioSangMui(ymd, hm, tu, sang) {
  if (!ymd || !hm) return null;
  if (!tu || !sang || tu === sang) return { ymd: String(ymd).slice(0, 10), hm: String(hm).slice(0, 5) };
  const moc = mocCuaGioTaiMui(ymd, hm, tu);
  return moc ? gioCuaMocTaiMui(moc, sang) : null;
}

/** Ngày 'YYYY-MM-DD' ở múi `tz`, lùi/tiến `n` ngày từ bây giờ. `null` nếu tên múi hỏng. */
export function ngayTaiMui(tz, n = 0, now = new Date()) {
  if (!tz) return null;
  try {
    const p = phanTaiMui(now.getTime(), tz);
    const d = new Date(Date.UTC(p.year, p.month - 1, p.day + n));
    return `${d.getUTCFullYear()}-${p2z(d.getUTCMonth() + 1)}-${p2z(d.getUTCDate())}`;
  } catch {
    return null;
  }
}
